import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/ui/Tooltip";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon alone tells a screen reader nothing. It is also the
      tip shown on hover and on focus. */
  label: string;
  icon: ReactNode;
  /**
   * No tip. For the cross that closes a dialog or a panel: it is the first
   * thing focus lands on when one opens, so its tip would open with it, and
   * Escape would then put the tip away instead of the dialog.
   */
  tipless?: boolean;
  variant?: "ghost" | "secondary";
  size?: "sm" | "md";
  onDark?: boolean;
}

/**
 * A button that is only an icon.
 *
 * Square, on the control grid: 36px beside an input, 32px inside a table row.
 * Ghost is the default because most of these sit in a row of content and
 * should not weigh more than the content does.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { label, icon, tipless = false, variant = "ghost", size = "md", onDark = false, className, ...props },
    ref,
  ) {
    const button = (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-sm",
          "transition-[background-color,box-shadow,color] duration-150",
          "disabled:cursor-not-allowed disabled:text-fg-placeholder disabled:shadow-none",
          size === "sm" ? "size-8 rounded-xs" : "size-9",
          onDark
            ? "bg-transparent text-board-muted hover:bg-white/10 hover:text-board-text"
            : variant === "ghost"
              ? "bg-transparent text-fg-muted hover:bg-ink-100 hover:text-fg active:bg-ink-200"
              : "bg-surface text-fg ring-1 ring-inset ring-line-strong hover:bg-ink-50 active:bg-ink-100",
          className,
        )}
        {...props}
      >
        <span aria-hidden="true">{icon}</span>
      </button>
    );
    return tipless ? button : <Tooltip label={label}>{button}</Tooltip>;
  },
);
